import type { BankAddressField } from '@/entities/bank-address';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { CreateBankAddressFormValues } from './types';
import { useEffect, useMemo, useRef } from 'react';
import { createBankAddressSchema } from './validation';
import { useTranslation } from 'react-i18next';

type Props = {
  fields: BankAddressField[];
  isLastStep: boolean;
};

export const useCreateBankAddressForm = ({ fields, isLastStep }: Props) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () => createBankAddressSchema(t, fields, isLastStep),
    [t, fields, isLastStep]
  );

  const form = useForm<CreateBankAddressFormValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      templateName: '',
    },
  });

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!fields.length || initializedRef.current) return;

    const values = fields.reduce(
      (acc, param) => {
        acc[param.id] = param.defaultValue || '';
        return acc;
      },
      { templateName: '' } as CreateBankAddressFormValues
    );

    form.reset(values);
    initializedRef.current = true;
  }, [fields, form]);

  return {
    form,
  };
};
